import React, { useState, useEffect } from 'react';
import {
  VictoryAxis,
  VictoryChart,
  VictoryScatter,
  VictoryLine,
  VictoryTheme,
  VictoryLegend,
} from 'victory';
import { useNavigate } from 'react-router-dom';
import SimpleHealthChart from './SimpleHealthChart';
import { useWellnessSummaryData } from '../hooks/wellnessSummaryData';

const VisualizaSummaryData = () => {
    console.log('VisualizaSummaryData component rendered');
    const navigate = useNavigate();
    const [patientId, setPatientId] = useState(null);
  const [healthData, setHealthData] = useState({
    step: {},
    heartrate: {},
    hrv: {}
  });

  // Get patient ID from localStorage
  useEffect(() => {
    console.log('Checking localStorage for user_info');
    if (localStorage.getItem('user_info') !== null) {
      const userInfo = JSON.parse(localStorage.getItem('user_info'));
      console.log('User info found:', userInfo);
      setPatientId(userInfo.di_id);
    } else {
      console.log('No user_info found in localStorage');
    }
  }, []);

  // Call the wellness summary API
  const { data: wellnessData, isLoading, error } = useWellnessSummaryData(patientId);
  console.log('API call result:', { patientId, wellnessData, isLoading, error });

  // Transform API data to the required format for last 8 days
  const transformApiData = (apiData) => {
    if (!apiData) return { step: {}, heartrate: {}, hrv: {} };

    // Generate the last 8 days starting from today
    const today = new Date();
    const last8Days = [];
    for (let i = 7; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      last8Days.push(date.toISOString().split('T')[0]); // YYYY-MM-DD format
    }

    const transformedData = {
      step: {},
      heartrate: {},
      hrv: {}
    };

    // Initialize all 8 days with empty objects (not null)
    last8Days.forEach(dateKey => {
      transformedData.step[dateKey] = { avg: null, min: null, max: null };
      transformedData.heartrate[dateKey] = { avg: null, min: null, max: null };
      transformedData.hrv[dateKey] = { avg: null, min: null, max: null };
    });

    // Transform step data (using dailysteps)
    if (apiData.dailysteps && apiData.dailysteps.week) {
      apiData.dailysteps.week.forEach((dayData) => {
        const dateKey = Object.keys(dayData)[0];
        const dataObj = dayData[dateKey];
        // Only include data for the last 8 days
        if (last8Days.includes(dateKey) && dataObj && dataObj.avg !== null && dataObj.avg !== undefined) {
          transformedData.step[dateKey] = {
            avg: parseFloat(dataObj.avg),
            min: dataObj.min !== null && dataObj.min !== undefined ? parseFloat(dataObj.min) : null,
            max: dataObj.max !== null && dataObj.max !== undefined ? parseFloat(dataObj.max) : null
          };
        }
      });
    }

    // Transform heart rate data
    if (apiData.heartrate && apiData.heartrate.week) {
      apiData.heartrate.week.forEach((dayData) => {
        const dateKey = Object.keys(dayData)[0];
        const dataObj = dayData[dateKey];
        // Only include data for the last 8 days
        if (last8Days.includes(dateKey) && dataObj && dataObj.avg !== null && dataObj.avg !== undefined) {
          transformedData.heartrate[dateKey] = {
            avg: parseFloat(dataObj.avg),
            min: dataObj.min !== null && dataObj.min !== undefined ? parseFloat(dataObj.min) : null,
            max: dataObj.max !== null && dataObj.max !== undefined ? parseFloat(dataObj.max) : null
          };
        }
      });
    }

    // Transform HRV data
    if (apiData.hrv && apiData.hrv.week) {
      apiData.hrv.week.forEach((dayData) => {
        const dateKey = Object.keys(dayData)[0];
        const dataObj = dayData[dateKey];
        // Only include data for the last 8 days
        if (last8Days.includes(dateKey) && dataObj && dataObj.avg !== null && dataObj.avg !== undefined) {
          const hrvData = {
            avg: parseFloat(dataObj.avg),
            min: dataObj.min !== null && dataObj.min !== undefined ? parseFloat(dataObj.min) : null,
            max: dataObj.max !== null && dataObj.max !== undefined ? parseFloat(dataObj.max) : null
          };
          transformedData.hrv[dateKey] = hrvData;
        }
      });
    }

    return transformedData;
  };

  // Update health data when API data changes
  useEffect(() => {
    if (wellnessData) {
      const transformedData = transformApiData(wellnessData);
      setHealthData(transformedData);
    }
  }, [wellnessData]);

  // Get appropriate unit and title for the data type
  const getDataTypeInfo = (type) => {
    switch (type) {
      case 'step':
        return { unit: 'steps', title: 'Daily Steps', color: '#3B82F6' };
      case 'heartrate':
        return { unit: 'bpm', title: 'Heart Rate', color: '#EF4444' };
      case 'hrv':
        return { unit: 'ms', title: 'Heart Rate Variability', color: '#10B981' };
      default:
        return { unit: '', title: 'Data', color: '#6B7280' };
    }
  };

  // Calculate statistics for each data type
  const calculateStats = (data) => {
    if (!data || Object.keys(data).length === 0) return null;
    
    const values = Object.values(data).map(d => d.avg).filter(v => v !== null);
    const minValues = Object.values(data).map(d => d.min).filter(v => v !== null);
    const maxValues = Object.values(data).map(d => d.max).filter(v => v !== null);
    
    if (values.length === 0) return null;
    
    return {
      avg: Math.round(values.reduce((sum, val) => sum + val, 0) / values.length),
      min: Math.min(...minValues),
      max: Math.max(...maxValues),
      trend: values[values.length - 1] > values[0] ? 'up' : values[values.length - 1] < values[0] ? 'down' : 'stable'
    };
  };

  const stepStats = calculateStats(healthData.step);
  const hrStats = calculateStats(healthData.heartrate);
  const hrvStats = calculateStats(healthData.hrv);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-500 via-blue-400 to-gray-100 pt-14">
        <div className="container mx-auto px-4 py-6">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">Health Data Summary</h1>
            <p className="text-blue-100">Loading health data...</p>
          </div>
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
          </div>
        </div>
      </div>
    );
  }

  // Add a fallback for when patientId is null
  if (!patientId) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-500 via-blue-400 to-gray-100 pt-14">
        <div className="container mx-auto px-4 py-6">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">Health Data Summary</h1>
            <p className="text-blue-100">No patient ID found</p>
          </div>
          <div className="text-center text-red-200">
            <p>Please log in to view health data.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-500 via-blue-400 to-gray-100 pt-14">
        <div className="container mx-auto px-4 py-6">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-white mb-2">Health Data Summary</h1>
            <p className="text-blue-100">Error loading health data</p>
          </div>
          <div className="text-center text-red-200">
            <p>Unable to load health data. Please try again later.</p>
            <p className="text-sm mt-2">Error: {error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-500 via-blue-400 to-gray-100 pt-14">
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Health Data Summary</h1>
          
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              onClick={() => navigate('/landing/questionanswer')}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
            >
              প্রাথমিক
            </button>
            <button
              onClick={() => navigate('/skin-disease')}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
            >
              চর্মরোগ
            </button>
            <button
              onClick={() => navigate('/landing/pregnancyriskeval')}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
            >
              গর্ভসমস্যা
            </button>
            <button
              onClick={() => navigate('/on-exam')}
              className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
            >
              হৃদরোগ
            </button>
          </div>
          
          <p className="text-gray-600">Most recent trend</p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {/* Steps Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Daily Steps</h3>
              <span className="text-2xl">🚶‍♂️</span>
            </div>
            {stepStats && (
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Average:</span>
                  <span className="font-semibold">{stepStats.avg.toLocaleString()} steps</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Range:</span>
                  <span className="font-semibold">{stepStats.min.toLocaleString()} - {stepStats.max.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Trend:</span>
                  <span className={`font-semibold ${
                    stepStats.trend === 'up' ? 'text-green-600' : 
                    stepStats.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {stepStats.trend === 'up' ? '↗️ Increasing' : 
                     stepStats.trend === 'down' ? '↘️ Decreasing' : '→ Stable'}
                  </span>
                </div>
              </div>
            )}
            {/* Steps Chart */}
            <div className="mt-4 h-48">
              <SimpleHealthChart
                dataType="step"
                unit="steps"
                data={healthData.step}
                color="#3B82F6"
                height={180}
                width={280}
              />
            </div>
          </div>

          {/* Heart Rate Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Heart Rate</h3>
              <span className="text-2xl">❤️</span>
            </div>
            {hrStats && (
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Average:</span>
                  <span className="font-semibold">{hrStats.avg} bpm</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Range:</span>
                  <span className="font-semibold">{hrStats.min} - {hrStats.max} bpm</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Trend:</span>
                  <span className={`font-semibold ${
                    hrStats.trend === 'up' ? 'text-red-600' : 
                    hrStats.trend === 'down' ? 'text-green-600' : 'text-gray-600'
                  }`}>
                    {hrStats.trend === 'up' ? '↗️ Increasing' : 
                     hrStats.trend === 'down' ? '↘️ Decreasing' : '→ Stable'}
                  </span>
                </div>
              </div>
            )}
            {/* Heart Rate Chart */}
            <div className="mt-4 h-48">
              <SimpleHealthChart
                dataType="heartrate"
                unit="bpm"
                data={healthData.heartrate}
                color="#EF4444"
                height={180}
                width={280}
              />
            </div>
          </div>

          {/* HRV Card */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Heart Rate Variability</h3>
              <span className="text-2xl">📊</span>
            </div>
            {hrvStats && (
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Average:</span>
                  <span className="font-semibold">{hrvStats.avg} ms</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Range:</span>
                  <span className="font-semibold">{hrvStats.min} - {hrvStats.max} ms</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Trend:</span>
                  <span className={`font-semibold ${
                    hrvStats.trend === 'up' ? 'text-green-600' : 
                    hrvStats.trend === 'down' ? 'text-red-600' : 'text-gray-600'
                  }`}>
                    {hrvStats.trend === 'up' ? '↗️ Increasing' : 
                     hrvStats.trend === 'down' ? '↘️ Decreasing' : '→ Stable'}
                  </span>
                </div>
              </div>
            )}
            {/* HRV Chart */}
            <div className="mt-4 h-48">
              <SimpleHealthChart
                dataType="hrv"
                unit="ms"
                data={healthData.hrv}
                color="#10B981"
                height={180}
                width={280}
              />
            </div>
          </div>
        </div>



        {/* Combined Chart */}
        <div className="mt-8 bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Combined Health Metrics</h3>
          <div className="h-80">
            <VictoryChart
              theme={VictoryTheme.material}
              domainPadding={20}
              height={300}
              width={800}
            >
                           <VictoryAxis
               dependentAxis={false}
               tickFormat={(t) => {
                 // Map numeric index to date labels
                 const dateLabels = Object.keys(healthData.step).sort().map(dateStr => {
                   const date = new Date(dateStr);
                   return `${date.getMonth() + 1}/${date.getDate()}`;
                 });
                 return dateLabels[t - 1] || '';
               }}
               style={{
                 tickLabels: { fontSize: 10, padding: 5 }
               }}
             />
             <VictoryAxis
               dependentAxis={true}
               style={{
                 tickLabels: { fontSize: 10, padding: 5 }
               }}
             />
              
              {/* Steps Line */}
              <VictoryLine
                data={Object.keys(healthData.step).sort().map((dateStr, index) => {
                  const data = healthData.step[dateStr];
                  return data && data.avg !== null && data.avg !== undefined ? { x: index + 1, y: data.avg / 100 } : null;
                }).filter(d => d !== null)}
                style={{
                  data: { stroke: "#3B82F6", strokeWidth: 2 }
                }}
              />
              <VictoryScatter
                data={Object.keys(healthData.step).sort().map((dateStr, index) => {
                  const data = healthData.step[dateStr];
                  return data && data.avg !== null && data.avg !== undefined ? { x: index + 1, y: data.avg / 100 } : null;
                }).filter(d => d !== null)}
                size={4}
                style={{
                  data: { fill: "#3B82F6" }
                }}
              />
              
              {/* Heart Rate Line */}
              <VictoryLine
                data={Object.keys(healthData.heartrate).sort().map((dateStr, index) => {
                  const data = healthData.heartrate[dateStr];
                  return data && data.avg !== null && data.avg !== undefined ? { x: index + 1, y: data.avg } : null;
                }).filter(d => d !== null)}
                style={{
                  data: { stroke: "#EF4444", strokeWidth: 2 }
                }}
              />
              <VictoryScatter
                data={Object.keys(healthData.heartrate).sort().map((dateStr, index) => {
                  const data = healthData.heartrate[dateStr];
                  return data && data.avg !== null && data.avg !== undefined ? { x: index + 1, y: data.avg } : null;
                }).filter(d => d !== null)}
                size={4}
                style={{
                  data: { fill: "#EF4444" }
                }}
              />
              
              {/* HRV Line */}
              <VictoryLine
                data={Object.keys(healthData.hrv).sort().map((dateStr, index) => {
                  const data = healthData.hrv[dateStr];
                  return data && data.avg !== null && data.avg !== undefined ? { x: index + 1, y: data.avg } : null;
                }).filter(d => d !== null)}
                style={{
                  data: { stroke: "#10B981", strokeWidth: 2 }
                }}
              />
              <VictoryScatter
                data={Object.keys(healthData.hrv).sort().map((dateStr, index) => {
                  const data = healthData.hrv[dateStr];
                  return data && data.avg !== null && data.avg !== undefined ? { x: index + 1, y: data.avg } : null;
                }).filter(d => d !== null)}
                size={4}
                style={{
                  data: { fill: "#10B981" }
                }}
              />
              
              <VictoryLegend
                x={600}
                y={50}
                orientation="vertical"
                gutter={20}
                style={{ border: { stroke: "black" }, title: { fontSize: 20 } }}
                data={[
                  { name: "Steps (scaled)", symbol: { fill: "#3B82F6" } },
                  { name: "Heart Rate", symbol: { fill: "#EF4444" } },
                  { name: "HRV", symbol: { fill: "#10B981" } }
                ]}
              />
            </VictoryChart>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VisualizaSummaryData; 